/**
 * THE source of truth for which solutions each deployment publishes, at which
 * URL, and under which label.
 *
 * The solutions twin of `product-catalog.ts`, and it exists for the same reason:
 * the header, footer and "Who We Serve" all built their links from
 * `ProductService.getSolutionList()`, so a solution belonging to another market
 * still rendered a full page on any domain that was handed its id. `/solutions/
 * 101/investidores` answered 200 on siila.com with Brazilian content.
 *
 * Two things differ from products:
 *
 * 1. **Every cross-market id has an exact local counterpart.** The same seven
 *    solutions exist in all three markets under different ids, so a foreign id
 *    is a 301 to the local equivalent, never a 404 — better for users and for
 *    link equity. The redirect table is therefore not hand-written: it is
 *    derived from {@link SOLUTION_KIND_BY_ID}, so all 24 rules follow from the
 *    seven `kind` assignments below and cannot drift out of sync.
 *
 * 2. **Labels live here too.** The CMS `shortName` records disagree across
 *    markets for the same solution ("REITS Investors" vs "REIT Investors",
 *    "Institutional Investor" vs the plural), and several carry stray leading or
 *    trailing spaces. Hardcoding the label is what makes the three link surfaces
 *    provably identical.
 *
 * CO is deliberately ABSENT and therefore ungated: it owns a separate record
 * block (115-121) that no sheet covers, so it keeps the existing CMS-driven
 * behavior. `isGatedSolutionPlatform` is the switch; callers must consult it
 * before treating an unresolved id as a 404.
 *
 * Ids, slugs and ordering are derived from `9.1.2026 Solutions - Correct URL.xlsx`
 * and verified against the live `/api/content/solution?countryCode=` records —
 * the array order below reproduces each market's CMS `norder`.
 *
 * Pure data + pure functions, no Angular — `SolutionCatalogService` is the
 * injectable wrapper that turns entries into localized links.
 */

/**
 * A solution's identity independent of the id it carries in a given market. The
 * same solution is 101 on BR and 108 on MX/LATAM, so every id-list check drifts
 * as markets are added; the cross-market redirects are generated from this.
 */
export type SolutionKind =
  | "institutional-investors"
  | "brokerage-firms"
  | "owners-operators-developers"
  | "tenants-corporate-occupiers"
  | "valuation-advisors"
  | "service-providers"
  | "reit-investors";

export interface SolutionCatalogEntry {
  /** CMS id behind the `solutions/:id/:name` detail route. */
  id: number;
  kind: SolutionKind;
  /**
   * `:name` slug per language, stored RAW — accents, commas and `&` included
   * (`owners,-operators-&-developers`), matching the sheet and the existing
   * `toUrlSlug` convention. `UrlCanonicalService` percent-encodes only the
   * crawler's `Location:` header.
   */
  slugs: { [lang: string]: string };
  /**
   * Visible label per language for the header, footer and "Who We Serve".
   * Replaces the CMS `shortName`, which is where the terminology drift lived.
   */
  labels: { [lang: string]: string };
}

/**
 * Per-deployment solution list, in the order it is rendered on all three
 * surfaces. Array order reproduces each market's CMS `norder`, so the visible
 * ordering is unchanged by the move off the API.
 */
export const SOLUTION_CATALOG: {
  [platformId: string]: SolutionCatalogEntry[];
} = {
  // BR — PT is the default language, EN lives under /lang/en.
  br: [
    {
      id: 101,
      kind: "institutional-investors",
      slugs: { pt: "investidores", en: "institutional-investors" },
      labels: { pt: "Investidores", en: "Institutional Investors" }
    },
    {
      id: 103,
      kind: "owners-operators-developers",
      slugs: {
        pt: "proprietários,-administradores-e-desenvolvedores",
        en: "owners,-operators-&-developers"
      },
      labels: {
        pt: "Proprietários e desenvolvedores",
        en: "Owners & Developers"
      }
    },
    {
      id: 104,
      kind: "tenants-corporate-occupiers",
      slugs: { pt: "ocupantes", en: "tenants-&-corporate-occupiers" },
      labels: { pt: "Ocupantes", en: "Tenants & Occupiers" }
    },
    {
      id: 102,
      kind: "brokerage-firms",
      slugs: {
        pt: "corretores-e-consultorias-imobiliárias",
        en: "brokerage-firms"
      },
      labels: { pt: "Consultorias imobiliárias", en: "Brokerage Firms" }
    },
    {
      id: 105,
      kind: "valuation-advisors",
      slugs: { pt: "avaliadores", en: "valuation-advisors" },
      labels: { pt: "Avaliadores", en: "Valuation Advisors" }
    },
    {
      id: 106,
      kind: "service-providers",
      slugs: { pt: "prestadores-de-serviços", en: "service-providers" },
      labels: { pt: "Prestadores de serviços", en: "Service Providers" }
    },
    {
      id: 107,
      kind: "reit-investors",
      slugs: { pt: "investidores-em-fiis", en: "reit-investors" },
      labels: { pt: "Investidores em FIIs", en: "REIT Investors" }
    }
  ],

  // MX — ES is the default language, EN lives under /lang/en.
  mx: [
    {
      id: 108,
      kind: "institutional-investors",
      slugs: {
        es: "inversionistas-institucionales",
        en: "institutional-investors"
      },
      labels: {
        es: "Inversionistas Institucionales",
        // Was the singular "Institutional Investor" in the CMS (ticket item D).
        en: "Institutional Investors"
      }
    },
    {
      id: 110,
      kind: "owners-operators-developers",
      slugs: {
        es: "propietarios-y-desarrolladores",
        en: "owners,-operators-&-developers"
      },
      labels: {
        es: "Propietarios y Desarrolladores",
        en: "Owners & Developers"
      }
    },
    {
      id: 111,
      kind: "tenants-corporate-occupiers",
      slugs: { es: "inquilinos", en: "tenants-&-corporate-occupiers" },
      labels: { es: "Inquilinos", en: "Tenants & Occupiers" }
    },
    {
      id: 109,
      kind: "brokerage-firms",
      slugs: { es: "firmas-de-brokerage", en: "brokerage-firms" },
      labels: { es: "Firmas de Brokerage", en: "Brokerage Firms" }
    },
    {
      id: 112,
      kind: "valuation-advisors",
      slugs: { es: "valuadores", en: "valuation-advisors" },
      labels: { es: "Valuadores", en: "Valuation Advisors" }
    },
    {
      id: 113,
      kind: "service-providers",
      slugs: { es: "proveedores-de-servicio", en: "service-providers" },
      labels: { es: "Proveedores de Servicio", en: "Service Providers" }
    },
    {
      id: 114,
      kind: "reit-investors",
      // The CMS name is "REITS Investors", which produced the slug
      // `reits-investors`. The sheet's canonical URL is `reit-investors`
      // (ticket item D), so the corrected spelling is authoritative here and
      // the old one 301s to it like any other non-canonical slug.
      slugs: { es: "inversionistas-en-reits", en: "reit-investors" },
      labels: { es: "Inversionistas en REITs", en: "REIT Investors" }
    }
  ],

  // LATAM — EN is the default language, ES lives under /lang/es.
  //
  // NOTE these are the MX-numbered records (108-113): `countryCode=LATAM`
  // returns 101-106, which `ProductService.LATAM_MIGRATED_SOLUTION_IDS` swaps
  // for the MX set at display time because only the MX records carry real
  // Spanish copy. The catalog encodes the post-migration result directly, which
  // is also what the sheet lists. REIT Investors is LATAM's own record, 122.
  latam: [
    {
      id: 108,
      kind: "institutional-investors",
      slugs: {
        en: "institutional-investors",
        es: "inversionistas-institucionales"
      },
      labels: {
        // Was the singular "Institutional Investor" in the CMS (ticket item D).
        en: "Institutional Investors",
        es: "Inversionistas Institucionales"
      }
    },
    {
      id: 110,
      kind: "owners-operators-developers",
      slugs: {
        en: "owners,-operators-&-developers",
        es: "propietarios-y-desarrolladores"
      },
      labels: {
        en: "Owners & Developers",
        es: "Propietarios y Desarrolladores"
      }
    },
    {
      id: 111,
      kind: "tenants-corporate-occupiers",
      slugs: { en: "tenants-&-corporate-occupiers", es: "inquilinos" },
      labels: { en: "Tenants & Occupiers", es: "Inquilinos" }
    },
    {
      id: 109,
      kind: "brokerage-firms",
      slugs: { en: "brokerage-firms", es: "firmas-de-brokerage" },
      labels: { en: "Brokerage Firms", es: "Firmas de Brokerage" }
    },
    {
      id: 112,
      kind: "valuation-advisors",
      slugs: { en: "valuation-advisors", es: "valuadores" },
      labels: { en: "Valuation Advisors", es: "Valuadores" }
    },
    {
      id: 113,
      kind: "service-providers",
      slugs: { en: "service-providers", es: "proveedores-de-servicio" },
      labels: { en: "Service Providers", es: "Proveedores de Servicio" }
    },
    {
      id: 122,
      kind: "reit-investors",
      slugs: { en: "reit-investors", es: "inversionistas-en-reits" },
      labels: {
        en: "REIT Investors",
        // The CMS reads "Inversionistas en FIBRAS" — MX-specific terminology on
        // a LATAM record, and at odds with its own `inversionistas-en-reits`
        // URL. Aligned with the REIT wording ticket item D establishes.
        es: "Inversionistas en REITs"
      }
    }
  ]
};

/**
 * Whether this deployment's `/solutions/*` routes are gated by the catalog.
 *
 * CO is not: it owns records 115-121, which no sheet covers, so it keeps the
 * CMS-driven list and renders whatever id it is given. Callers MUST check this
 * before reading an unresolved id as a 404 — on CO there is nothing to resolve
 * against.
 */
export function isGatedSolutionPlatform(platformId: string): boolean {
  return !!SOLUTION_CATALOG[platformId];
}

/** Every entry this deployment publishes, in render order. */
export function getSolutionCatalog(platformId: string): SolutionCatalogEntry[] {
  return SOLUTION_CATALOG[platformId] || [];
}

/**
 * Which solution each id addresses, across every GATED market.
 *
 * Derived from the catalogs rather than written out, so the 24 cross-market
 * redirect rules follow from the seven `kind` assignments and cannot drift.
 * CO's 115-121 are deliberately absent: they belong to no gated market, so on
 * BR/MX/LATAM they resolve to nothing and answer 404 (ticket item C).
 */
export const SOLUTION_KIND_BY_ID: { [id: number]: SolutionKind } = Object.keys(
  SOLUTION_CATALOG
).reduce((acc, platformId) => {
  SOLUTION_CATALOG[platformId].forEach(entry => {
    acc[entry.id] = entry.kind;
  });
  return acc;
}, {} as { [id: number]: SolutionKind });

/** The entry with this id on this deployment, or `null`. */
export function findSolutionEntryById(
  platformId: string,
  id: number
): SolutionCatalogEntry {
  const match = getSolutionCatalog(platformId).find(entry => entry.id === id);
  return match || null;
}

/** This deployment's entry for a given solution, or `null`. */
export function findSolutionEntryByKind(
  platformId: string,
  kind: SolutionKind
): SolutionCatalogEntry {
  const match = getSolutionCatalog(platformId).find(
    entry => entry.kind === kind
  );
  return match || null;
}

/**
 * Resolve a requested `solutions/:id/:name` id to the entry that should answer
 * it, or `null` when this deployment publishes no equivalent — the caller's cue
 * to answer 404.
 *
 * Unlike products this is keyed on the id ALONE: every cross-market solution has
 * an exact local counterpart, so there is no ambiguity for a slug to resolve. A
 * local id returns itself (the caller then corrects the slug); a foreign id
 * returns the local entry for the same solution (a 301); anything else — CO's
 * 115-121, a retired record, a typo — returns `null`.
 *
 * Only meaningful on a gated platform; see {@link isGatedSolutionPlatform}.
 */
export function resolveSolutionEntry(
  platformId: string,
  id: number
): SolutionCatalogEntry {
  if (id == null || isNaN(id) || !isGatedSolutionPlatform(platformId)) {
    return null;
  }
  const local = findSolutionEntryById(platformId, id);
  if (local) {
    return local;
  }
  const kind = SOLUTION_KIND_BY_ID[id];
  return kind ? findSolutionEntryByKind(platformId, kind) : null;
}

/** Fold a language code to the key the catalog stores (`en-mx` -> `en`). */
export function normalizeSolutionLang(lang: string): string {
  if (!lang) {
    return "en";
  }
  if (lang === "en-mx") {
    return "en";
  }
  if (lang === "pt-br") {
    return "pt";
  }
  return lang;
}

/**
 * The entry's suffixless path for `lang`, WITHOUT a leading slash and without
 * the `/lang/<code>` segment — `buildLocalizedPath` owns that decision. Falls
 * back to the first declared slug for a language the market does not serve.
 */
export function getSolutionBasePath(
  entry: SolutionCatalogEntry,
  lang: string
): string {
  if (!entry) {
    return null;
  }
  const key = normalizeSolutionLang(lang);
  const slug = entry.slugs[key] || entry.slugs[Object.keys(entry.slugs)[0]];
  return `solutions/${entry.id}/${slug}`;
}

/** The entry's visible label for `lang`, with the same fallback rule. */
export function getSolutionLabel(
  entry: SolutionCatalogEntry,
  lang: string
): string {
  if (!entry) {
    return "";
  }
  const key = normalizeSolutionLang(lang);
  return entry.labels[key] || entry.labels[Object.keys(entry.labels)[0]];
}

/**
 * Pull the id out of a `/solutions/<id>/<slug>` path, or `null` when the path is
 * not an id-driven solution URL. Any `/lang/<code>` tail and query string are
 * ignored.
 */
export function parseSolutionDetailId(path: string): number {
  if (!path) {
    return null;
  }
  const segments = path
    .split("?")[0]
    .split("#")[0]
    .replace(/^\/+/, "")
    .split("/");
  if (segments[0] !== "solutions" || !/^\d+$/.test(segments[1] || "")) {
    return null;
  }
  return +segments[1];
}
